import { Check, ArrowRight, CheckCircle2, Headphones } from "lucide-react";
import { pricing, textureBackground } from "../data/courseData";

export const PricingCard = ({
  onBuy,
  onEnterStudio,
  isPaying = false,
  hasActiveEntitlement = false,
  isEntitlementLoading = false,
}) => {
  const ctaDisabled = isPaying || isEntitlementLoading;

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16"
    >
      {/* Texture bg */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src={textureBackground}
          alt=""
          className="w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-14 md:mb-20">
          <div className="text-xs uppercase tracking-[0.25em] text-[#C5A059] font-semibold mb-4">
            {hasActiveEntitlement ? "Access active" : "Investment"}
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
            {hasActiveEntitlement ? "You own access." : "One subscription."}
            <br />
            <span className="gold-text italic font-light">
              {hasActiveEntitlement ? "Enter the studio." : "Every session."}
            </span>
          </h2>
        </div>

        <div className="max-w-xl mx-auto">
          <div
            data-testid="pricing-card"
            className="relative rounded-3xl border border-[#C5A059]/25 bg-[#0D0D0F] p-8 md:p-12 overflow-hidden"
            style={{
              boxShadow:
                "0 30px 100px -30px rgba(197,160,89,0.18), 0 0 0 1px rgba(197,160,89,0.06)",
            }}
          >
            {/* Inner gold gradient accent */}
            <div
              aria-hidden
              className="absolute -top-32 -right-32 w-72 h-72 rounded-full opacity-25 blur-[80px]"
              style={{ background: "#C5A059" }}
            />

            {/* Badge */}
            <div className="absolute top-6 right-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">
                {hasActiveEntitlement ? "Purchased" : pricing.badge}
              </span>
            </div>

            <div className="relative">
              <div className="text-sm uppercase tracking-[0.2em] text-zinc-400 font-semibold">
                {hasActiveEntitlement ? "Studio access" : pricing.plan}
              </div>

              <div className="mt-6 flex items-baseline gap-4">
                {/* Original price + badge to show limited-time 50% drop */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-zinc-400 line-through">₹3,199</span>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-[#C5A059] bg-[#C5A059]/10 rounded-full">
                    50% OFF
                  </span>
                </div>

                <span className="font-heading text-6xl md:text-7xl font-bold text-white tracking-tighter">
                  {hasActiveEntitlement ? "Active" : pricing.priceLabel}
                </span>
              </div>

              <p className="mt-4 text-zinc-400 text-sm leading-relaxed max-w-md">
                {hasActiveEntitlement
                  ? "Your account already has studio access. Continue directly to the protected masterclass without starting checkout again."
                  : pricing.description}
              </p>

              <div className="my-8 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              <ul className="space-y-4">
                {pricing.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-zinc-200">
                    <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[#C5A059]/15 flex items-center justify-center">
                      <Check
                        className="w-3 h-3 text-[#C5A059]"
                        strokeWidth={2.5}
                      />
                    </span>
                    <span className="text-sm md:text-base font-light">{f}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid="pricing-buy-now"
                onClick={hasActiveEntitlement ? onEnterStudio : onBuy}
                disabled={ctaDisabled}
                className="mt-10 w-full group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#C5A059] text-black font-medium text-sm tracking-wide hover:bg-[#E5BE6B] transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100 shadow-[0_8px_40px_-8px_rgba(197,160,89,0.5)]"
              >
                {hasActiveEntitlement ? (
                  <>
                    Enter Studio
                    <Headphones className="w-4 h-4" strokeWidth={2} />
                  </>
                ) : (
                  <>
                    {isEntitlementLoading
                      ? "Checking access..."
                      : isPaying
                        ? "Opening checkout..."
                        : "Buy Now"}
                    <ArrowRight
                      className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"
                      strokeWidth={2}
                    />
                  </>
                )}
              </button>

              {hasActiveEntitlement && (
                <div className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#C5A059]/25 bg-[#C5A059]/10 px-4 py-3 text-sm font-medium text-[#E3C985]">
                  <CheckCircle2 className="h-4 w-4" />
                  Access granted on this account
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
